import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ExternalLink, ArrowRight, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return '/placeholder.jpg';
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w800`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w800`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w800`;
  
  return url;
};

// Collapsible News Component
const CollapsibleNews = ({ title, items, convertUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mt-12 border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        <ChevronDown 
          size={18} 
          className={`text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && (
        <div className="p-4 border-t border-border">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.slice(0, 6).map((item) => (
              <Link 
                key={item.content_id}
                to={`/${item.slug}`}
                className="block group"
                data-testid={`content-${item.slug}`}
              >
                <div className="relative aspect-[16/9] bg-black overflow-hidden mb-3">
                  <img
                    src={convertUrl(item.image)}
                    alt={item.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest mb-1">
                  <span>{item.category || item.type}</span>
                  {item.source && (
                    <>
                      <span>·</span>
                      <span>{item.source}</span>
                    </>
                  )}
                </div>
                <h4 className="text-sm editorial-serif group-hover:accent-red transition-colors line-clamp-2">
                  {item.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [content, setContent] = useState([]);
  const [brandSites, setBrandSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesRes, contentRes, brandSitesRes] = await Promise.all([
          fetch(`${API_URL}/api/companies`),
          fetch(`${API_URL}/api/content`),
          fetch(`${API_URL}/api/brand-sites/public`).catch(() => null)
        ]);
        
        if (companiesRes.ok) {
          const data = await companiesRes.json();
          setCompanies(data);
        }
        if (contentRes.ok) {
          const data = await contentRes.json();
          setContent(data);
        }
        if (brandSitesRes?.ok) {
          const data = await brandSitesRes.json();
          setBrandSites(data);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get content items related to a specific company (including from brand sites)
  const getCompanyContent = (companyName, companyId, companyProjects = []) => {
    // Direct content matches
    const directContent = content.filter(item => {
      if (item.company && item.company.toLowerCase().includes(companyName.toLowerCase())) {
        return true;
      }
      if (item.project && companyProjects.some(p => 
        item.project.toLowerCase().includes(p.toLowerCase())
      )) {
        return true;
      }
      return false;
    });
    
    // Find brand sites that link to this company and get their content
    const linkedBrandSiteContent = [];
    brandSites.forEach(site => {
      const isLinked = (site.sections || []).some(section => 
        (section.linked_companies || []).some(c => 
          c.company_id === companyId || c.name?.toLowerCase() === companyName.toLowerCase()
        )
      );
      
      if (isLinked) {
        const siteContent = content.filter(item => 
          item.site === site.slug && 
          !directContent.some(dc => dc.content_id === item.content_id)
        );
        linkedBrandSiteContent.push(...siteContent);
      }
    });
    
    return [...directContent, ...linkedBrandSiteContent];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="companies-page">
      {/* Hero Section */}
      <section className="editorial-container py-12 lg:py-20 border-b border-border">
        <div className="max-w-4xl">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold editorial-serif mb-6 leading-tight">
            Companies
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            The operating entities within the Cultural Revolution ecosystem. 
            Each focused on execution within their domain.
          </p>
        </div>
      </section>

      {/* Companies List */}
      <section className="editorial-container py-12 lg:py-16">
        {companies.length === 0 ? (
          <p className="text-center text-muted-foreground">No companies found.</p>
        ) : (
          <div className="space-y-20">
            {companies.map((company) => {
              const companyContent = getCompanyContent(company.name, company.company_id, company.projects || []);
              
              return (
                <div 
                  key={company.company_id} 
                  className="border-b border-border pb-16 last:border-0"
                  data-testid={`company-${company.slug}`}
                >
                  <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
                    {/* Company Image / Logo */}
                    <div className="lg:col-span-2">
                      <div className="relative aspect-[4/3] bg-black overflow-hidden flex items-center justify-center p-8">
                        {company.logo ? (
                          <img
                            src={convertGoogleDriveUrl(company.logo)}
                            alt={`${company.name} logo`}
                            className="max-w-full max-h-full object-contain"
                            data-testid={`company-logo-${company.slug}`}
                          />
                        ) : company.image ? (
                          <img
                            src={convertGoogleDriveUrl(company.image)}
                            alt={company.name}
                            className="w-full h-full object-cover opacity-90"
                            data-testid={`company-image-${company.slug}`}
                          />
                        ) : (
                          <div className="text-4xl font-bold text-muted-foreground opacity-50">
                            {company.name.charAt(0)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Company Info */}
                    <div className="lg:col-span-3 space-y-6">
                      <div>
                        {(company.tagline || company.type) && (
                          <p className="text-xs font-bold uppercase tracking-widest accent-red mb-2">
                            {company.tagline || company.type}
                          </p>
                        )}
                        <h2 
                          className="text-3xl lg:text-4xl font-bold editorial-serif mb-4"
                          data-testid={`company-name-${company.slug}`}
                        >
                          {company.name}
                        </h2>
                        {company.description && (
                          <p className="text-base text-foreground/80 leading-relaxed">
                            {company.description}
                          </p>
                        )}
                      </div>

                      {/* Location & Founded */}
                      {(company.location || company.founded) && (
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {company.location && (
                            <span>📍 {company.location}</span>
                          )}
                          {company.founded && (
                            <span>Founded {company.founded}</span>
                          )}
                        </div>
                      )}

                      {/* Projects */}
                      {company.projects && company.projects.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-muted-foreground">
                            Projects
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {company.projects.map((project, idx) => (
                              <span 
                                key={idx} 
                                className="text-xs px-3 py-1 bg-muted text-foreground/70"
                              >
                                {project}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* External Link */}
                      {company.website && (
                        <div>
                          <a 
                            href={company.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            data-testid={`company-website-${company.slug}`}
                          >
                            <Button variant="outline">
                              Visit Site
                              <ExternalLink size={16} className="ml-2" />
                            </Button>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Related Content - Collapsible */}
                  {companyContent.length > 0 && (
                    <CollapsibleNews 
                      title={`Related Coverage & Updates (${companyContent.length})`}
                      items={companyContent}
                      convertUrl={convertGoogleDriveUrl}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Context Section */}
      <section className="editorial-container py-16 lg:py-24 border-t border-border">
        <div className="editorial-narrow text-center">
          <h2 className="text-3xl lg:text-4xl font-bold editorial-serif mb-4">
            The Framework in Action
          </h2>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            These companies operate independently while sharing infrastructure, learnings, 
            and momentum. Cultural Revolution orchestrates how they align.
          </p>
          <Link to="/about">
            <Button size="lg" data-testid="learn-more-btn">
              Learn More About the System
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CompaniesPage;
